// Pré-checagem de exclusividade entre CategoriaLiga, feita ANTES de o atleta avançar
// para o checkout (PRD nivel-categoria-liga, AC-7..AC-15).
//
// IMPORTANTE — esta é uma checagem de UX, NÃO a autoridade da regra. O backend
// (`InscricaoTorneioService.validarExclusividadeCategoriaLiga`) continua sendo quem
// decide e bloqueia de fato; ele é a única fronteira confiável, já que uma chamada
// direta à API não passa por esta tela. O objetivo aqui é só não deixar o atleta
// atravessar duas telas para descobrir no final que a combinação é proibida.
//
// A regra replicada é a mesma do backend, e propositalmente conservadora:
//   - só compara categorias de torneio VINCULADAS a uma CategoriaLiga (sem vínculo passa);
//   - mesma CategoriaLiga nunca conflita;
//   - CategoriaLigas distintas conflitam, a menos que estejam marcadas como combináveis;
//   - a relação combinável é simétrica no banco (o service grava os dois sentidos), mas
//     aqui checamos os DOIS lados mesmo assim — se um dos lados vier sem a coleção
//     carregada, a checagem ainda funciona pelo outro.
//
// O que esta função NÃO faz (de propósito): não conhece a isenção de organizador/admin.
// Um organizador que se inscreva por esta tela verá o aviso, mas o backend aceitaria a
// inscrição. Preferimos avisar a mais na tela do atleta a deixar passar silenciosamente —
// a isenção existe para o fluxo administrativo, não para a auto-inscrição.

export interface CategoriaLigaRef {
  id: string;
  nome?: string | null;
  combinaveis?: { id: string }[] | null;
}

export interface CategoriaComLiga {
  id: string;
  nome: string;
  categoriaLiga?: CategoriaLigaRef | null;
}

export interface InscricaoExistenteComLiga {
  categoriaTorneio?: {
    id: string;
    nome: string;
    categoriaLiga?: CategoriaLigaRef | null;
  } | null;
}

export interface ConflitoCategoriaLiga {
  /** Categoria DE TORNEIO que o atleta está tentando selecionar agora. */
  categoriaNome: string;
  /** Categoria DE TORNEIO que gera o conflito — já inscrita ou também selecionada. */
  conflitaComNome: string;
  /** `true` quando o conflito é com uma inscrição JÁ existente; `false` entre duas seleções. */
  contraInscricaoExistente: boolean;
}

// Nota deliberada: o conflito NÃO carrega o nome da CategoriaLiga. CategoriaLiga é um
// conceito de organização da liga — o atleta não a vê em lugar nenhum do app e não precisa
// saber que existe. Expor "(CL Alfa)" numa mensagem de erro vazaria vocabulário interno e
// só geraria dúvida. O que identifica o problema para o atleta é o nome da categoria DO
// TORNEIO, que é o que ele selecionou na tela.

function saoCombinaveis(a: CategoriaLigaRef, b: CategoriaLigaRef): boolean {
  const aTemB = (a.combinaveis ?? []).some((c) => c.id === b.id);
  const bTemA = (b.combinaveis ?? []).some((c) => c.id === a.id);
  return aTemB || bTemA;
}

/**
 * Retorna todos os conflitos entre as categorias selecionadas e (a) as inscrições ativas
 * que o atleta já tem no torneio e (b) as demais categorias selecionadas no mesmo fluxo.
 *
 * `inscricoesExistentes` deve vir JÁ filtrada por status ativo (≠ CANCELADA) — é o que a
 * tela do torneio faz antes de passar a lista adiante, e mantém esta função sem
 * dependência do enum de status.
 */
export function encontrarConflitosCategoriaLiga(
  selecionadas: CategoriaComLiga[],
  inscricoesExistentes: InscricaoExistenteComLiga[],
): ConflitoCategoriaLiga[] {
  const conflitos: ConflitoCategoriaLiga[] = [];

  // (a) selecionada × já inscrita
  for (const sel of selecionadas) {
    const clSel = sel.categoriaLiga;
    if (!clSel) continue;
    for (const insc of inscricoesExistentes) {
      const ctExistente = insc.categoriaTorneio;
      const clExistente = ctExistente?.categoriaLiga;
      if (!ctExistente || !clExistente) continue;
      // Reinscrever-se na MESMA categoria de torneio é outro erro (duplicidade), tratado
      // em outro ponto; aqui só nos interessa o conflito ENTRE categorias.
      if (ctExistente.id === sel.id) continue;
      if (clExistente.id === clSel.id) continue;
      if (saoCombinaveis(clSel, clExistente)) continue;
      conflitos.push({
        categoriaNome: sel.nome,
        conflitaComNome: ctExistente.nome,
        contraInscricaoExistente: true,
      });
    }
  }

  // (b) selecionada × selecionada (mesmo fluxo) — cada par uma única vez
  for (let i = 0; i < selecionadas.length; i += 1) {
    for (let j = i + 1; j < selecionadas.length; j += 1) {
      const a = selecionadas[i];
      const b = selecionadas[j];
      const clA = a.categoriaLiga;
      const clB = b.categoriaLiga;
      if (!clA || !clB) continue;
      if (clA.id === clB.id) continue;
      if (saoCombinaveis(clA, clB)) continue;
      conflitos.push({
        categoriaNome: a.nome,
        conflitaComNome: b.nome,
        contraInscricaoExistente: false,
      });
    }
  }

  return conflitos;
}

/**
 * Mensagem em Português explicando um conflito, pronta para exibir ao atleta.
 *
 * Nomeia apenas as categorias DO TORNEIO — nunca a CategoriaLiga por trás delas (ver a nota
 * em `ConflitoCategoriaLiga`).
 */
export function mensagemConflito(conflito: ConflitoCategoriaLiga): string {
  const alvo = `"${conflito.categoriaNome}"`;
  const outra = `"${conflito.conflitaComNome}"`;
  return conflito.contraInscricaoExistente
    ? `Você já possui inscrição ativa em ${outra} neste torneio, que não pode ser combinada com ${alvo}.`
    : `${alvo} e ${outra} não podem ser disputadas pelo mesmo atleta neste torneio.`;
}
